<script lang="ts">
  // ManoEspecialBanner — banner inline na action-bar durante mao de 11
  // (truco locked com UM time no threshold). Substitui o action prompt
  // normal nesse periodo.
  //
  // Pra player do TIME EM 11: mostra botoes FICAR / FUGIR + countdown.
  //   - FICAR: nao envia comando, so fecha local. Player joga
  //     normalmente quando o turno chega.
  //   - FUGIR: envia game:mao:fugir. Backend valida que nenhuma carta
  //     foi jogada ainda. Oponente ganha 1 ponto.
  //   - Timer expirando = auto-fica (server joga carta aleatoria via
  //     turn timer normal).
  // Pra player do TIME OPONENTE: mostra "Adversario decidindo... Xs".
  //
  // Em 2v2, AMBOS do time em 11 veem o banner (qualquer um pode fugir).
  // Cartas do parceiro ficam reveladas (face-up) — backend libera via
  // canSeeHand quando ManoEspecialActive, OppHand renderiza com
  // revealCards={true}.

  import { onDestroy } from 'svelte';
  import type { MatchSnapshot } from '../../stores/match';
  import { send } from '../../stores/ws';
  import { userId } from '../../stores/auth';
  import { speak } from '../../stores/speech';
  import { markMyRunDecision } from '../../stores/narration';

  type Props = { snap: MatchSnapshot };
  let { snap }: Props = $props();

  // Time do user (em 2v2). Em 1v1, mineIds = [meu id].
  let mineIds = $derived.by((): string[] => {
    if (snap.format === '2v2') {
      const a = (snap.team_a || []).map((p) => p.id);
      const b = (snap.team_b || []).map((p) => p.id);
      return a.includes($userId) ? a : b.includes($userId) ? b : [];
    }
    return [$userId];
  });

  // Estamos em mao de 11 PENDENTE (ainda nao decidiu) e o time decidindo
  // inclui o user? Usa mano_especial_pending (nao active) pra banner sumir
  // assim que alguem do time aceita ou foge.
  let isMyTeam = $derived(
    snap.mano_especial_pending === true &&
      !!snap.mano_especial_player_id &&
      mineIds.includes(snap.mano_especial_player_id),
  );
  let isOppTeam = $derived(
    snap.mano_especial_pending === true &&
      !!snap.mano_especial_player_id &&
      !mineIds.includes(snap.mano_especial_player_id),
  );

  // Countdown via deadline do turn timer (auto-fica = jogar carta).
  let now = $state(Date.now());
  let tickId: number | null = null;
  $effect(() => {
    if ((isMyTeam || isOppTeam) && snap.deadline) {
      if (tickId === null) {
        tickId = window.setInterval(() => (now = Date.now()), 250);
      }
    } else if (tickId !== null) {
      clearInterval(tickId);
      tickId = null;
    }
  });
  onDestroy(() => {
    if (tickId !== null) clearInterval(tickId);
  });
  // Conta turn_duration_sec a partir de quando o deadline CHEGA no snap
  // (tempo local), NAO do deadline ISO absoluto — igual o PlayerPortrait.
  // Como este componente recebe o displaySnap (que tem o hold da animacao
  // de fim-de-mao), o countdown so comeca quando o banner aparece, em vez de
  // ja vir ~7s adiantado pela animacao do deal. Felipe 2026-06-16.
  let turnDur = $derived(snap.turn_duration_sec ?? 10);
  let lastDeadlineKey = $state('');
  let timerStartedAt = $state(0);
  $effect(() => {
    const k = snap.deadline ?? '';
    if (k !== lastDeadlineKey) {
      lastDeadlineKey = k;
      timerStartedAt = Date.now();
    }
  });
  let remainingSec = $derived(
    snap.deadline && timerStartedAt
      ? Math.max(0, Math.ceil(turnDur - (now - timerStartedAt) / 1000))
      : 0,
  );
  let danger = $derived(remainingSec > 0 && remainingSec <= 5);

  let decided = $state(false);
  // Reseta `decided` em cada NOVA mao (round_number incrementa). Antes
  // usavamos transicao false→true de pending — mas em maos de 11
  // CONSECUTIVAS (time foge mas continua em 11), pending vai true→true
  // sem nunca ir pra false. Round_number sempre muda entre maos.
  // Felipe 2026-05-20.
  let prevRound = $state(snap.round_number ?? 0);
  $effect(() => {
    const rn = snap.round_number ?? 0;
    if (rn !== prevRound) {
      decided = false;
      prevRound = rn;
    }
  });
  function fugir() {
    if (decided) return;
    decided = true;
    speak($userId, 'TÔ FORA!', 'run');
    markMyRunDecision();
    send('game:mao:fugir');
  }
  function ficar() {
    if (decided) return;
    decided = true;
    speak($userId, 'BORA!', 'ack');
    send('game:mao:aceitar');
  }
</script>

{#if isMyTeam && snap.status === 'running'}
  <div class="mano-banner mine">
    <div class="label">
      <span class="title">MÃO DE 11</span>
      {#if remainingSec > 0}
        <span class="countdown" class:danger>{remainingSec}s</span>
      {/if}
    </div>
    {#if !decided}
      <div class="actions">
        <button type="button" class="btn stay" onclick={ficar}>FICAR</button>
        <button type="button" class="btn flee" onclick={fugir}>FUGIR</button>
      </div>
    {:else}
      <div class="waiting">aguardando jogada…</div>
    {/if}
  </div>
{:else if isOppTeam && snap.status === 'running'}
  <div class="mano-banner opp">
    <span class="title">MÃO DE 11 — adversário decidindo</span>
    {#if remainingSec > 0}
      <span class="countdown" class:danger>{remainingSec}s</span>
    {/if}
  </div>
{/if}

<style>
  .mano-banner {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    border-radius: var(--radius-md);
    border: 1px solid rgba(255, 210, 74, 0.55);
    background: linear-gradient(135deg, rgba(255, 210, 74, 0.12), rgba(255, 142, 60, 0.12));
    box-shadow: 0 0 14px rgba(255, 210, 74, 0.25);
    width: 100%;
  }
  .label {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
  }
  .title {
    font-size: 12px;
    letter-spacing: 2px;
    color: #ffd24a;
    font-weight: 700;
    text-transform: uppercase;
  }
  .countdown {
    font-size: 13px;
    font-weight: 700;
    padding: 2px 7px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.08);
    color: var(--text);
  }
  .countdown.danger {
    background: rgba(255, 76, 126, 0.25);
    color: var(--danger);
  }
  .actions {
    display: flex;
    gap: 8px;
  }
  .btn {
    padding: 6px 14px;
    border-radius: 999px;
    border: 1px solid;
    cursor: pointer;
    font-weight: 700;
    font-size: 12px;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    transition: background 0.15s, transform 0.1s;
  }
  .btn:active { transform: scale(0.97); }
  .btn.stay {
    background: rgba(108, 255, 189, 0.15);
    color: var(--ok);
    border-color: var(--ok);
  }
  .btn.stay:hover { background: rgba(108, 255, 189, 0.3); }
  .btn.flee {
    background: rgba(255, 76, 126, 0.15);
    color: var(--danger);
    border-color: var(--danger);
  }
  .btn.flee:hover { background: rgba(255, 76, 126, 0.3); }
  .waiting {
    font-size: 11px;
    letter-spacing: 1.5px;
    color: var(--text-dim);
  }

  .mano-banner.opp {
    border-color: rgba(180, 210, 255, 0.4);
    background: rgba(0, 0, 0, 0.35);
    box-shadow: none;
    justify-content: space-between;
  }
  .mano-banner.opp .title {
    color: var(--text-dim);
    font-weight: 600;
  }
</style>
